import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DragDropContext } from 'react-beautiful-dnd';
import { CircularProgress, Box, Typography, Button, TextField } from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';
import KanbanColumn from './KanbanColumn';
import { useKanbanTasks } from '../../hooks/useKanbanTasks';
import { useKanbanSocket } from '../../hooks/useKanbanSocket';
import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import toastUtils from '../../utils/toastUtils';
import KanbanBulkTaskModal from './KanbanBulkTaskModal';

const useStyles = makeStyles(theme => ({
  board: {
    display: 'flex',
    overflowX: 'auto',
    ...theme.scrollbarStyles,
    padding: theme.spacing(1),
    minHeight: '70vh',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    flexDirection: 'column',
  },
  emptyBoard: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    flexDirection: 'column',
    color: theme.palette.text.secondary,
  },
  addLaneContainer: {
    minWidth: 272,
    maxWidth: 272,
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  addLaneForm: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  addLaneButton: {
    minHeight: 100,
    border: '2px dashed',
    borderColor: theme.palette.divider,
    borderRadius: 12,
    width: '100%',
    color: theme.palette.text.secondary,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const KanbanBoard = ({
  ticketId = null,
  selectedTasks = [],
  onTaskSelection,
  viewMode = 'board',
  onTaskCreated,
  onLaneUpdated,
  onCardMove,
  updateTicket,
  filters = {},
  lanes: propLanes = []
}) => {
  const classes = useStyles();
  const {
    tasks,
    loading,
    fetchTasks,
    moveTask,
  } = useKanbanTasks(ticketId);

  const [lanes, setLanes] = useState([]);
  const [isAddingLane, setIsAddingLane] = useState(false);
  const [newLaneTitle, setNewLaneTitle] = useState('');
  const [showBulkTaskModal, setShowBulkTaskModal] = useState(false);
  const [bulkTaskTickets, setBulkTaskTickets] = useState([]);
  
  // Integração em tempo real
  const handleTaskUpdate = (updatedTask) => {
    fetchTasks(filters);
  };
  
  const handleTaskCreate = (newTask) => {
    fetchTasks(filters);
    if (onTaskCreated) onTaskCreated(newTask);
  };
  
  const handleTaskDelete = (taskId) => {
    fetchTasks(filters);
  };
  
  const handleTaskMove = (movedTask) => {
    fetchTasks(filters);
  };
  
  useKanbanSocket(
    handleTaskUpdate,
    handleTaskCreate,
    handleTaskDelete,
    handleTaskMove
  );
  
  // Listener para evento customizado de criação de tarefas em lote
  useEffect(() => {
    const handleOpenBulkTaskModal = (event) => {
      const { selectedTickets } = event.detail;
      setBulkTaskTickets(selectedTickets);
      setShowBulkTaskModal(true);
    };

    window.addEventListener('openBulkTaskModal', handleOpenBulkTaskModal);
    
    return () => {
      window.removeEventListener('openBulkTaskModal', handleOpenBulkTaskModal);
    };
  }, []);

  // Organizar tasks em lanes
  useEffect(() => {
    if (propLanes.length > 0) {
      // Usar lanes vindas do prop com tickets organizados por tags
      const lanesWithTasks = propLanes.map(lane => {
        // Para cada ticket na lane, buscar suas tarefas
        const laneTasks = [];
        if (lane.tickets) {
          lane.tickets.forEach(ticket => {
            const ticketTasks = tasks.filter(task => task.ticketId === ticket.id);
            laneTasks.push(...ticketTasks);
          });
        }
        
        return {
          ...lane,
          tasks: laneTasks,
          tickets: lane.tickets || []
        };
      });
      
      setLanes(lanesWithTasks);
    } else if (tasks && tasks.length > 0) {
      // Organizar tarefas por status se não houver lanes
      const defaultLanes = [
        { id: 'todo', title: i18n.t('kanban.tasks.status.todo'), color: '#e3f2fd' },
        { id: 'in_progress', title: i18n.t('kanban.tasks.status.in_progress'), color: '#fff3e0' },
        { id: 'done', title: i18n.t('kanban.tasks.status.done'), color: '#e8f5e8' },
        { id: 'blocked', title: i18n.t('kanban.tasks.status.blocked'), color: '#ffebee' },
      ];
      
      const organizedLanes = defaultLanes.map(lane => ({
        ...lane,
        tasks: tasks.filter(task => (task.status || 'todo') === lane.id),
        tickets: []
      }));
      
      setLanes(organizedLanes);
    } else {
      setLanes([]);
    }
  }, [tasks, propLanes]);

  // Função para lidar com o drag and drop de tickets/tarefas
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;

    // Se não mudou de lane e nem de posição, não faz nada
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    try {
      // Se houver lanes vindas do prop (sistema de tickets com tags), usar onCardMove
      if (propLanes.length > 0 && onCardMove) {
        await onCardMove(draggableId, destination.droppableId);
      }
      // Caso contrário, usar sistema de tasks
      else {
        await moveTask(
          draggableId,
          destination.droppableId,
          source.index,
          destination.index
        );
      }
    } catch (error) {
      console.error('Error moving card:', error);
    }
  };

  // Função para adicionar nova lane
  const addLane = async () => {
    if (!newLaneTitle.trim()) {
      toastUtils.error(i18n.t('kanban.lane.nameRequired'), 'Erro');
      return;
    }

    try {
      // Buscar user do AuthContext se estiver disponível
      const userData = JSON.parse(localStorage.getItem('user') || '{}');

      await api.post('/tags', {
        name: newLaneTitle.trim(),
        color: '#757575',
        kanban: 1,
        userId: userData.id,
        timeLane: 0,
        nextLaneId: null,
        greetingMessageLane: '',
        rollbackLaneId: null
      });

      toastUtils.success(i18n.t('kanban.lane.created'), 'Sucesso');
      setNewLaneTitle('');
      setIsAddingLane(false);

      // Refresh lanes
      if (onLaneUpdated) onLaneUpdated();
    } catch (error) {
      console.error('Error creating lane:', error);
      toastUtils.error(i18n.t('kanban.lane.createError'), 'Erro');
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
        <Typography variant="body1" style={{ marginTop: 16 }}>
          {i18n.t('kanban.loading')}
        </Typography>
      </div>
    );
  }

  if (!lanes || lanes.length === 0) {
    return (
      <div className={classes.emptyBoard}>
        <Typography variant="h6">
          {i18n.t('kanban.noLanes')}
        </Typography>
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className={classes.board}>
        {lanes.map((lane) => (
          <KanbanColumn
            key={lane.id}
            id={lane.id}
            title={lane.name || lane.title}
            color={lane.color}
            tasks={lane.tasks || []}
            tickets={lane.tickets || []}
            updateTicket={updateTicket}
            onTaskSelection={onTaskSelection}
            selectedTasks={selectedTasks}
            onTaskCreated={onTaskCreated}
            onLaneUpdated={onLaneUpdated}
          />
        ))}

        {/* Add Lane Section */}
        <div className={classes.addLaneContainer}>
          {isAddingLane ? (
            <div className={classes.addLaneForm}>
              <TextField
                label={i18n.t('kanban.lane.addLane')}
                value={newLaneTitle}
                onChange={(e) => setNewLaneTitle(e.target.value)}
                placeholder={i18n.t('kanban.lane.enterTitle')}
                fullWidth
                variant="outlined"
                size="small"
                style={{ marginBottom: 16 }}
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addLane();
                  }
                }}
              />
              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={addLane}
                  disabled={!newLaneTitle.trim()}
                >
                  {i18n.t('kanban.lane.add')}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setIsAddingLane(false);
                    setNewLaneTitle('');
                  }}
                >
                  {i18n.t('kanban.lane.cancel')}
                </Button>
              </Box>
            </div>
          ) : (
            <Button
              variant="outlined"
              className={classes.addLaneButton}
              onClick={() => setIsAddingLane(true)}
              startIcon={<AddIcon />}
            >
              <Box display="flex" flexDirection="column" alignItems="center">
                <Typography variant="body2" style={{ marginTop: 8 }}>
                  {i18n.t('kanban.lane.addLane')}
                </Typography>
              </Box>
            </Button>
          )}
        </div>
      </div>
      
      {/* Bulk Task Creation Modal */}
      <KanbanBulkTaskModal
        open={showBulkTaskModal}
        onClose={() => setShowBulkTaskModal(false)}
        selectedTickets={bulkTaskTickets}
        onTasksCreated={(tasks) => {
          fetchTasks(filters);
          if (onTaskCreated) onTaskCreated(tasks[0]); // Passar a primeira tarefa criada
        }}
      />
    </DragDropContext>
  );
};

export default KanbanBoard;
